import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';

import { RouterLink } from '@angular/router';
import { AppError } from '@domain-utils/app-error';
import { HtlmActionStatus } from '@helpers/html/action-status';
import { AppPageLayoutComponent } from '@view/shared/templates/page-layout/page-layout.component';
import { AppInputOption } from '@helpers/input/input.option';
import { ProcessingLoadComponent } from '../components/processing-load/processing-load.component';
import { TypeOperations } from '@view/shared/constants/typeOperation.const';
import { UploadSkeletonComponent } from '../components/upload-skeleton/upload-skeleton.component';
import { UploadStepOneComponent } from '../components/upload-step-one/upload-step-one.component';
import { UploadTemplateCardComponent } from '../components/upload-template-card/upload-template-card.component';
import { UploadFileDropComponent } from '../components/upload-file-drop/upload-file-drop.component';

import { AppNotificationService } from '@service/notification/app-notification.service';
import { MESSAGE_CONSTANTS } from '@view/shared/constants/message.const';
import { BulkUploadsGateway } from '@service/bulk-uploads/bulk-uploads.gateway';
import {
  UploadIssue,
  UploadProcessResult
} from '@model/bulk-load/upload-result.model';
import { BULK_UPLOAD_TITLE } from '@view/shared/constants/template.const';
import { catchError, EMPTY, finalize, Observable, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const requiredValidator = (control: AbstractControl): ValidationErrors | null =>
  Validators.required(control);
const NEW_POLICY_OPERATION_ID = '001';

@Component({
  selector: 'app-bulk-risk-upload',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AppPageLayoutComponent,
    ProcessingLoadComponent,
    RouterLink,
    UploadSkeletonComponent,
    UploadStepOneComponent,
    UploadTemplateCardComponent,
    UploadFileDropComponent
  ],
  templateUrl: './bulk-risk-upload.component.html',
  styleUrl: './bulk-risk-upload.component.scss'
})
export class BulkRiskUploadComponent implements OnInit {
  private readonly bulkUploadsGateway = inject(BulkUploadsGateway);
  private readonly notificationService = inject(AppNotificationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly file = signal<File | null>(null);
  readonly state = signal<HtlmActionStatus>(HtlmActionStatus.Initial);
  readonly loadId = signal<string | null>(null);
  readonly title = signal(BULK_UPLOAD_TITLE);
  readonly errorUploadFile = signal<UploadIssue | null>(null);
  readonly hasFile = computed(() => !!this.file());
  readonly isLoading = signal(true);
  readonly isDownloadingTemplate = signal(false);
  private readonly fileSizeValue = computed(() => {
    const selectedFile = this.file();
    if (!selectedFile) return '';
    return `${(selectedFile.size / 1024 / 1024).toFixed(1)} MB`;
  });
  form: FormGroup;
  itemsTypeOperations = TypeOperations;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.nonNullable.group({
      typeOperation: [null as AppInputOption | null, requiredValidator],
      policyNumber: [
        null as string | null,
        [
          requiredValidator,
          Validators.pattern(/^\d+$/),
          Validators.minLength(12),
          Validators.maxLength(12)
        ]
      ]
    });
  }

  ngOnInit(): void {
    this.registerTypeOperationChanges();
    setTimeout(() => {
      this.isLoading.set(false);
    }, 500);
  }

  selectFile(file: File | null): boolean {
    if (!file) return false;

    this.file.set(file);
    return true;
  }

  resetFile(): void {
    this.file.set(null);
  }

  resetState(): void {
    this.title.set(BULK_UPLOAD_TITLE);
    this.loadId.set(null);
    this.state.set(HtlmActionStatus.Initial);
    this.errorUploadFile.set(null);
    this.file.set(null);
  }

  processUpload(operationType: string, policyNumber: string): void {
    const selectedFile = this.file();

    if (!operationType || !selectedFile) {
      this.notificationService.error(MESSAGE_CONSTANTS.MISSING_TYPE_OR_FILE);
      return;
    }

    this.setLoadingState();

    this.bulkUploadsGateway
      .processUpload(operationType, policyNumber, selectedFile)
      .subscribe({
        next: (result) => this.applyUploadResult(result),
        error: (error) => this.handleUploadError(error)
      });
  }

  downloadTemplateDynamic(operationType: string, policyNumber: string): void {
    this.isDownloadingTemplate.set(true);
    this.bulkUploadsGateway
      .downloadTemplateBase64(operationType, policyNumber)
      .pipe(finalize(() => this.isDownloadingTemplate.set(false)))
      .subscribe({
        next: (base64) => {
          this.bulkUploadsGateway.downloadFile(base64);
          this.notificationService.success(
            MESSAGE_CONSTANTS.TEMPLATE_DOWNLOADED
          );
        },
        error: () => {
          this.notificationService.error(
            MESSAGE_CONSTANTS.TEMPLATE_DOWNLOAD_ERROR
          );
        }
      });
  }

  downloadErrorFile(): void {
    const uploadError = this.errorUploadFile();
    if (!uploadError?.fileBase64) {
      this.notificationService.error(MESSAGE_CONSTANTS.TEMPLATE_DOWNLOAD_ERROR);
      return;
    }

    this.bulkUploadsGateway.downloadFile(
      uploadError.fileBase64,
      uploadError.fileName ?? this.bulkUploadsGateway.getTemplateFileName()
    );
  }

  onFileSelected(file: File | null): void {
    this.selectFile(file);
  }

  resetForm(): void {
    this.resetFile();
  }

  get fileSizeMB(): string {
    return this.fileSizeValue();
  }

  onNewLoad(): void {
    this.form.reset();
    this.resetForm();
    this.resetState();
  }

  onRetryLoad(): void {
    const existingLoadId = this.loadId();
    if (existingLoadId) {
      this.retryUploadOnly(existingLoadId);
    } else {
      this.initUpload();
    }
  }

  private retryUploadOnly(loadId: string): void {
    const selectedFile = this.file();
    if (!selectedFile) {
      this.notificationService.error(MESSAGE_CONSTANTS.MISSING_TYPE_OR_FILE);
      return;
    }

    this.setLoadingState();

    this.bulkUploadsGateway.retryUploadFile(selectedFile, loadId).subscribe({
      next: (result) => this.applyUploadResult(result, loadId),
      error: (error) => this.handleUploadError(error)
    });
  }

  onDownloadErrorTemplate(): void {
    this.downloadErrorFile();
  }

  downloadTemplate(): void {
    const typeOperation = this.TypeOperation;
    const policyNumber = this.PolicyNumber;
    this.downloadTemplateDynamic(typeOperation?.id ?? '', policyNumber);
  }

  initUpload(): void {
    const typeOperation = this.TypeOperation;
    const policyNumber = this.PolicyNumber;
    this.processUpload(typeOperation?.id ?? '', policyNumber);
  }

  get TypeOperation(): AppInputOption {
    return this.form.get('typeOperation')?.value as AppInputOption;
  }

  get PolicyNumber(): string {
    return this.form.get('policyNumber')?.value as string;
  }

  get isStepOneIncomplete(): boolean {
    return !this.isTypeOperationValid || !this.isPolicyNumberReady;
  }

  get isUploadAreaDisabled(): boolean {
    return this.isStepOneIncomplete || !!this.hasFile();
  }

  get isDownloadDisabled(): boolean {
    return (
      this.isStepOneIncomplete ||
      !!this.hasFile() ||
      this.isDownloadingTemplate()
    );
  }

  get isUploadDisabled(): boolean {
    return !this.hasFile() || this.isStepOneIncomplete;
  }

  private registerTypeOperationChanges(): void {
    this.form
      .get('typeOperation')
      ?.valueChanges.pipe(
        tap((operation) =>
          this.preparePolicyNumberControl(operation as AppInputOption | null)
        ),
        switchMap((operation) =>
          this.generatePolicyNumber(operation as AppInputOption | null)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((policyNumber) => {
        this.policyNumberControl.setValue(policyNumber, {
          emitEvent: false
        });
        this.policyNumberControl.markAsPristine();
        this.policyNumberControl.markAsUntouched();
      });
  }

  private preparePolicyNumberControl(operation: AppInputOption | null): void {
    if (this.isNewPolicyOperation(operation)) {
      this.policyNumberControl.reset(null, { emitEvent: false });
      this.policyNumberControl.disable({ emitEvent: false });
      return;
    }

    this.policyNumberControl.enable({ emitEvent: false });
    this.policyNumberControl.reset(null, { emitEvent: false });
  }

  private generatePolicyNumber(
    operation: AppInputOption | null
  ): Observable<string> {
    const operationType = operation?.id ?? '';

    if (operationType !== NEW_POLICY_OPERATION_ID) {
      return EMPTY;
    }

    return this.bulkUploadsGateway.generatePolicyNumber(operationType).pipe(
      catchError((error: unknown) => {
        this.policyNumberControl.reset(null, { emitEvent: false });
        this.notifyPolicyGenerationError(error);
        return EMPTY;
      })
    );
  }

  private notifyPolicyGenerationError(error: unknown): void {
    if (error instanceof AppError && error.root?.message) {
      this.notificationService.error(error.root.message);
      return;
    }

    this.notificationService.error(
      MESSAGE_CONSTANTS.POLICY_NUMBER_GENERATION_ERROR,
      true
    );
  }

  private get isTypeOperationValid(): boolean {
    return !!this.form.get('typeOperation')?.valid;
  }

  private get isPolicyNumberReady(): boolean {
    if (this.isNewPolicyOperation(this.TypeOperation)) {
      return !!this.PolicyNumber?.trim();
    }

    return !!this.policyNumberControl.valid;
  }

  private isNewPolicyOperation(operation: AppInputOption | null): boolean {
    return operation?.id === NEW_POLICY_OPERATION_ID;
  }

  private get policyNumberControl(): AbstractControl {
    return this.form.get('policyNumber')!;
  }

  private applyUploadResult(
    result: UploadProcessResult,
    fallbackLoadId = ''
  ): void {
    this.loadId.set(result.loadId || fallbackLoadId);

    if (result.hasErrors && result.issue) {
      this.loadId.set(null);
      this.errorUploadFile.set(result.issue);
      this.state.set(HtlmActionStatus.Error);
      return;
    }

    this.state.set(HtlmActionStatus.Success);
  }

  private handleUploadError(error: unknown): void {
    if (!(error instanceof AppError)) {
      this.notificationService.error(MESSAGE_CONSTANTS.UPLOAD_GENERIC_ERROR);
    }

    this.state.set(HtlmActionStatus.Error);
  }

  private setLoadingState(): void {
    this.state.set(HtlmActionStatus.Loading);
    this.title.set('');
    this.errorUploadFile.set(null);
  }
}
